import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Dashboard.css';
import TaskModal from '../components/TaskModal';

const API_URL = '/api';

function Dashboard({ stats, tasks, phases }) {
  const [budgetData, setBudgetData] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTasks, setModalTasks] = useState([]);
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [recentInvoices, setRecentInvoices] = useState([]);

  useEffect(() => {
    loadBudgetData();
    loadExtras();
  }, []);

  const loadBudgetData = async () => {
    try {
      const response = await axios.get(`${API_URL}/budget/summary`);
      setBudgetData(response.data);
    } catch (error) {
      console.error('Erreur chargement budget:', error);
    }
  };

  const loadExtras = async () => {
    try {
      const [aRes, iRes] = await Promise.all([
        axios.get(`${API_URL}/appointments`),
        axios.get(`${API_URL}/invoices`)
      ]);
      const now = new Date();
      const upcoming = (aRes.data || []).filter(a => {
        const d = a.start_at ? new Date(a.start_at) : null;
        return d && !isNaN(d.getTime()) && d >= now;
      }).sort((x,y) => new Date(x.start_at) - new Date(y.start_at)).slice(0,5);
      setUpcomingAppointments(upcoming);

      const invoices = (iRes.data || []).slice().sort((a,b) => {
        const da = a.invoice_date ? new Date(a.invoice_date) : new Date(a.created_at || 0);
        const db = b.invoice_date ? new Date(b.invoice_date) : new Date(b.created_at || 0);
        return db - da;
      }).slice(0,5);
      setRecentInvoices(invoices);
    } catch (err) {
      console.error('Erreur chargement extras:', err);
    }
  };
  // Calculer les stats localement à partir des tâches et phases
  const computeStats = () => {
    const totalTasks = tasks.length;
    const completedTasks = tasks.filter(t => t.status === 'Terminé').length;
    const inProgressTasks = tasks.filter(t => t.status === 'En cours').length;
    const todoTasks = tasks.filter(t => t.status === 'A faire').length;
    const avgProgress = totalTasks > 0 
      ? Math.round(tasks.reduce((sum, t) => sum + (t.progress || 0), 0) / totalTasks)
      : 0;
    const totalEstimatedCost = tasks.reduce((sum, t) => sum + (t.cost || 0), 0);

    return {
      total_tasks: totalTasks,
      completed_tasks: completedTasks,
      in_progress_tasks: inProgressTasks,
      todo_tasks: todoTasks,
      avg_progress: avgProgress,
      total_estimated_cost: totalEstimatedCost
    };
  };

  const computeTasksByPriority = () => {
    const priorities = [
      { priority: 1, count: tasks.filter(t => t.priority === 1).length },
      { priority: 2, count: tasks.filter(t => t.priority === 2).length },
      { priority: 3, count: tasks.filter(t => t.priority === 3).length }
    ];
    return priorities;
  };

  const computePhaseProgress = () => {
    return phases.map(phase => {
      const phaseTasks = tasks.filter(t => t.phase_id === phase.id);
      const completedTasks = phaseTasks.filter(t => t.status === 'Terminé').length;
      const avgProgress = phaseTasks.length > 0
        ? Math.round(phaseTasks.reduce((sum, t) => sum + (t.progress || 0), 0) / phaseTasks.length)
        : 0;
      
      return {
        nom: phase.nom,
        total_tasks: phaseTasks.length,
        completed_tasks: completedTasks,
        avg_progress: avgProgress
      };
    });
  };

  const statData = computeStats();
  const tasksByPriority = computeTasksByPriority();
  const phaseProgress = computePhaseProgress();

  const completionRate = statData.total_tasks > 0
    ? Math.round((statData.completed_tasks / statData.total_tasks) * 100)
    : 0;

  const openTasks = (filterFn) => {
    const selection = tasks.filter(filterFn);
    setModalTasks(selection);
    setModalOpen(true);
  };

  return (
    <div className="dashboard">
      <h2>📊 Tableau de Bord - Suivi Global</h2>

      {/* Cartes KPI */}
      <div className="grid grid-4">
        <div className="stat-card total" onClick={() => openTasks(() => true)} style={{ cursor: 'pointer' }}>
          <div className="stat-icon">📋</div>
          <div className="stat-content">
            <div className="stat-value">{statData?.total_tasks || 0}</div>
            <div className="stat-label">Total Tâches</div>
          </div>
        </div>

        <div className="stat-card completed" onClick={() => openTasks(t => t.status === 'Terminé')} style={{ cursor: 'pointer' }}>
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <div className="stat-value">{statData?.completed_tasks || 0}</div>
            <div className="stat-label">Terminées</div>
          </div>
        </div>

        <div className="stat-card inprogress" onClick={() => openTasks(t => t.status === 'En cours')} style={{ cursor: 'pointer' }}>
          <div className="stat-icon">⚙️</div>
          <div className="stat-content">
            <div className="stat-value">{statData?.in_progress_tasks || 0}</div>
            <div className="stat-label">En Cours</div>
          </div>
        </div>

        <div className="stat-card todo" onClick={() => openTasks(t => t.status === 'A faire')} style={{ cursor: 'pointer' }}>
          <div className="stat-icon">📌</div>
          <div className="stat-content">
            <div className="stat-value">{statData?.todo_tasks || 0}</div>
            <div className="stat-label">À Faire</div>
          </div>
        </div>
      </div>

      {/* Progression globale */}
      <div className="card">
        <h3>📈 Progression Générale</h3>
        <div className="large-progress">
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${completionRate}%` }}></div>
          </div>
          <div className="completion-text">{completionRate}% complété</div>
        </div>
      </div>

      {/* Phases */}
      <div className="card">
        <h3>🏗️ Progression par Phase</h3>
        <div className="phases-list">
          {phaseProgress && phaseProgress.map(phase => {
            const phaseCompletion = phase.total_tasks > 0
              ? Math.round((phase.completed_tasks / phase.total_tasks) * 100)
              : 0;
            
            return (
              <div key={phase.nom} className="phase-item">
                <div className="phase-name">{phase.nom}</div>
                <div className="phase-stats">
                  <small>{phase.completed_tasks}/{phase.total_tasks} tâches</small>
                </div>
                <div className="phase-progress">
                  <div className="progress-bar">
                    <div className="progress-fill" style={{ width: `${phaseCompletion}%` }}></div>
                  </div>
                  <span className="progress-percent">{phaseCompletion}%</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Priorités */}
      <div className="grid grid-2">
        <div className="card">
          <h3>🎯 Tâches par Priorité</h3>
          <div className="priority-list">
            {tasksByPriority && tasksByPriority.map(p => {
              const labels = ['🔴 Urgentes', '🟡 Normales', '🟢 Basses'];
              return (
                <div key={p.priority} className="priority-item">
                  <span>{labels[p.priority - 1]}</span>
                  <span className="count">{p.count}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="card">
          <h3>💰 Budget Estimé</h3>
          <div className="budget-display">
            <div className="budget-amount">
              {statData?.total_estimated_cost?.toFixed(2) || '0.00'}€
            </div>
            <small>Coût total estimé de tous les travaux</small>
          </div>
        </div>

        <div className="card">
          <h3>💸 Dépenses Réelles</h3>
          {budgetData?.summary?.total_spent ? (
            <div className="budget-display">
              <div className="budget-amount spent">
                {budgetData.summary.total_spent.toFixed(2) || '0.00'} €
              </div>
              <small>{budgetData.summary.categories} catégories</small>
            </div>
          ) : (
            <div className="empty-state">
              <p>Aucune dépense enregistrée</p>
            </div>
          )}
        </div>
      </div>

      {/* Tâches récentes */}
      <div className="card">
        <h3>📝 Tâches Urgentes</h3>
        <div className="urgent-tasks">
          {tasks && tasks
            .filter(t => t.priority === 1 && t.status !== 'Terminé' && t.status !== 'Devis')
            .slice(0, 5)
            .map(task => (
              <div key={task.id} className="urgent-item" onClick={() => openTasks(t => t.id === task.id)} style={{ cursor: 'pointer' }}>
                <div className="urgent-title">{task.title}</div>
                <div className="urgent-meta">
                  <span className="phase-tag">{task.phase_name}</span>
                  <span className="status-tag" style={{
                    backgroundColor: task.status === 'En cours' ? '#2196f3' : '#ff9800'
                  }}>
                    {task.status}
                  </span>
                </div>
              </div>
            ))}
        </div>
      </div>

      <div className="grid grid-2">
        <div className="card">
          <h3>📅 Prochains rendez-vous</h3>
          {upcomingAppointments.length === 0 ? (
            <div className="empty-state">Aucun rendez-vous prévu</div>
          ) : (
            <div className="mini-list">
              {upcomingAppointments.map(a => (
                <div key={a.id} className="mini-item">
                  <div className="mini-left">
                    <div className="mini-title">{a.title}</div>
                    <div className="muted">{a.contact_name || '—'}</div>
                  </div>
                  <div className="mini-right muted">{new Date(a.start_at).toLocaleString()}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card">
          <h3>📄 Dernières factures</h3>
          {recentInvoices.length === 0 ? (
            <div className="empty-state">Aucune facture</div>
          ) : (
            <div className="mini-list">
              {recentInvoices.map(inv => (
                <div key={inv.id} className="mini-item">
                  <div className="mini-left">
                    <div className="mini-title">{inv.vendor || inv.filename || 'Facture'}</div>
                    <div className="muted">{inv.description || ''}</div>
                  </div>
                  <div className="mini-right muted">{inv.amount ? `${inv.amount.toFixed ? inv.amount.toFixed(2) : inv.amount} €` : ''}<br/>{inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString() : ''}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Dépenses par catégorie */}
      {budgetData?.byCategory && budgetData.byCategory.length > 0 && (
        <div className="card">
          <h3>📊 Dépenses par Catégorie</h3>
          <div className="categories-summary">
            {budgetData.byCategory.slice(0, 6).map(cat => (
              <div key={cat.category} className="category-summary-item">
                <div className="category-name">{cat.category}</div>
                <div className="category-info">
                  <span className="category-amount">{cat.total.toFixed(2)} €</span>
                  <span className="category-badge">{cat.count}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      
      <TaskModal open={modalOpen} onClose={() => setModalOpen(false)} tasks={modalTasks} />
    </div>
  );
}

export default Dashboard;
